import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Promo Context ─────────────────────────────────────────────────────────────
export const PromoContext = createContext({ active: false, discount_percent: 25, discount_services: [] });

export function PromoProvider({ children }) {
  const [promo, setPromo] = useState({ active: false, discount_percent: 25, discount_services: [] });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/promo-status`)
      .then(r => setPromo(r.data))
      .catch(() => {});
  }, []);

  return <PromoContext.Provider value={promo}>{children}</PromoContext.Provider>;
}

export function usePromo() {
  return useContext(PromoContext);
}

// ── Helper: extract numeric price from strings like "$200" or "200" ──────────
function parsePrice(priceStr) {
  if (typeof priceStr === 'number') return priceStr;
  const m = String(priceStr).replace(/,/g, '').match(/[\d.]+/);
  return m ? parseFloat(m[0]) : 0;
}

// ── DiscountedPrice component ─────────────────────────────────────────────────
// Props:
//   price     — string or number, e.g. "$200" or 200
//   category  — service key, e.g. "Window Tint" / "Vinyl Wrap" / "Tint Removal" etc.
//   className — optional extra className on wrapper
//   size      — 'sm' | 'md' | 'lg' (controls text sizes)
export default function DiscountedPrice({ price, category, className = '', size = 'md' }) {
  const { active, discount_percent, discount_services } = usePromo();
  const base = parsePrice(price);
  const discounted = active && discount_services.includes(category) && base > 0;
  const discountedAmt = discounted ? Math.round(base * (1 - discount_percent / 100)) : base;

  const sizes = {
    sm: { orig: 'text-xs', disc: 'text-sm font-bold' },
    md: { orig: 'text-sm', disc: 'text-base font-bold' },
    lg: { orig: 'text-base', disc: 'text-xl font-extrabold' },
  };
  const s = sizes[size] || sizes.md;

  if (!discounted) {
    return <span className={className}>${base.toLocaleString()}</span>;
  }

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <span className={`${s.orig} text-zinc-500 line-through`}>${base.toLocaleString()}</span>
      <span className={`${s.disc} text-amber-400`}>${discountedAmt.toLocaleString()}</span>
    </span>
  );
}
